// The phone's connection to the kit on the PC, through the relay: a `Transport` for
// `VoiceKitClient`, so the phone page runs the same client, transcript and audio code as any
// other UI. Ported from live-voice-ai's RelaySocket (app/src/web/relay-socket.ts):
// end-to-end encryption per leg (e2e.ts), reconnect with backoff, wake on return from the
// background, and mic audio dropped (never queued) when the uplink is backed up. Inside the
// leg it speaks PROTOCOL.md: `hello` on every (re)connect, JSON events, binary audio.

import { pcm16Bytes, PROTOCOL_VERSION, type ClientFrame, type ServerMsg } from "../protocol";
import type { Transport, TransportHandlers } from "../transport";
import { b64u, KIND_BINARY, KIND_TEXT, LegCipher, randomBytes, unb64u } from "./e2e";

export type RelayStatus = "connecting" | "open" | "pending" | "pc-offline" | "revoked" | "closed";

export interface RelayOptions {
  /** `wss://<relay>/client`. */
  url: string;
  /** `<deskId>.<deviceId>.<secret>` from pairing. */
  token: string;
  c2d: string;
  d2c: string;
  name?: string;
  WebSocketImpl?: typeof WebSocket;
  retryMs?: (attempt: number, code: number) => number;
  /** Mic audio is dropped while more than this many bytes wait to be sent. */
  maxBuffered?: number;
}

export class RelayTransport implements Transport {
  readonly kind = "relay" as const;
  /** The relay-level state (pairing pending, PC offline, revoked), beyond open/closed. */
  onRelayStatus: (status: RelayStatus) => void = () => {};
  relayStatus: RelayStatus = "closed";

  private ws: WebSocket | null = null;
  private cipher: LegCipher | null = null;
  private ready = false;
  private stopped = true;
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private dialSeq = 0; // only the newest dial may open a socket
  private handlers: TransportHandlers | null = null;
  private sent = 0;
  private dropped = 0;
  private readonly WS: typeof WebSocket;
  private readonly retryMs: (attempt: number, code: number) => number;
  private readonly maxBuffered: number;
  private readonly enc = new TextEncoder();
  private readonly dec = new TextDecoder();

  constructor(private readonly opts: RelayOptions) {
    this.WS = opts.WebSocketImpl ?? WebSocket;
    this.retryMs = opts.retryMs ?? ((n, code) => (code === 4409 ? 3000 : Math.min(30000, 1000 * 2 ** n)));
    // Only a hopeless backlog drops audio: Safari's bufferedAmount isn't always trustworthy,
    // and at ~32 KB/s of mic audio a real backlog is rare.
    this.maxBuffered = opts.maxBuffered ?? 8 * 1024 * 1024;
  }

  get open(): boolean {
    return this.ready && this.ws?.readyState === this.WS.OPEN;
  }

  connect(handlers: TransportHandlers): void {
    this.handlers = handlers;
    this.stopped = false;
    this.attempt = 0;
    void this.dial();
  }

  /** "Try again": dial now, dropping a dial still hanging or a backoff still waiting. */
  retry(): void {
    if (!this.stopped && !this.open) {
      this.attempt = 0;
      void this.dial();
    }
  }

  /** Call when the page becomes visible again: iOS suspends sockets in the background. */
  wake(): void {
    const connecting = this.ws !== null && this.ws.readyState === this.WS.CONNECTING;
    if (!this.stopped && !this.open && !connecting) {
      this.attempt = 0;
      void this.dial();
    }
  }

  send(msg: ClientFrame): void {
    if (!this.open) {
      this.handlers?.onMessage({ type: "error", code: "disconnected", message: "not connected to your PC", re: msg.id });
      return;
    }
    this.seal(KIND_TEXT, this.enc.encode(JSON.stringify(msg)));
  }

  sendAudio(pcm: Int16Array): boolean {
    const ws = this.ws;
    // Late mic audio is worse than none: drop it rather than queue behind a slow uplink.
    if (!this.open || !ws || ws.bufferedAmount > this.maxBuffered) {
      this.dropped++;
      return false;
    }
    this.sent++;
    this.seal(KIND_BINARY, pcm16Bytes(pcm));
    return true;
  }

  close(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.ready = false;
    const ws = this.ws;
    this.ws = null;
    ws?.close();
    this.setRelayStatus("closed");
    this.handlers?.onStatus("closed");
  }

  stats(): Record<string, unknown> {
    return { sentAudio: this.sent, droppedAudio: this.dropped, buffered: this.ws?.bufferedAmount ?? null, relay: this.relayStatus };
  }

  private seal(kind: number, data: Uint8Array): void {
    const ws = this.ws;
    const cipher = this.cipher;
    if (!ws || !cipher) return;
    // Seals resolve in call order, so frames go out in order.
    void cipher.seal(kind, data).then((frame) => {
      if (this.ws === ws && this.ready) ws.send(frame);
    });
  }

  private setRelayStatus(s: RelayStatus): void {
    this.relayStatus = s;
    this.onRelayStatus(s);
  }

  private async dial(): Promise<void> {
    const seq = ++this.dialSeq;
    if (this.timer) clearTimeout(this.timer);
    const old = this.ws;
    this.ws = null;
    old?.close();
    this.ready = false;
    this.setRelayStatus("connecting");
    this.handlers?.onStatus("connecting");
    const salt = randomBytes(16);
    const cipher = await LegCipher.create(unb64u(this.opts.c2d), unb64u(this.opts.d2c), salt, "client");
    if (this.stopped || seq !== this.dialSeq) return; // a newer dial superseded this one
    const ws = new this.WS(this.opts.url);
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    this.cipher = cipher;
    ws.onopen = () => ws.send(JSON.stringify({ t: "attach", token: this.opts.token, salt: b64u(salt) }));
    ws.onmessage = (e: MessageEvent) => {
      if (this.ws !== ws) return;
      if (typeof e.data === "string") {
        if (!this.ready && e.data.includes('"ready"')) {
          this.ready = true;
          this.attempt = 0;
          // Every leg is a new client of the kit: introduce ourselves first.
          this.seal(
            KIND_TEXT,
            this.enc.encode(
              JSON.stringify({
                type: "hello",
                protocol: PROTOCOL_VERSION,
                role: "ui",
                name: this.opts.name ?? "phone",
                events: true,
                audio: { mic: true, speaker: true },
                // Files sent with a turn (1.5), guests (1.8), pause (1.9) and what it missed while
                // away (1.11). Kits that serve phone legs grant theirs in `welcome.features`;
                // older ones don't.
                features: ["attachments", "guests", "pause", "conversation_recent"],
              }),
            ),
          );
          this.setRelayStatus("open");
          this.handlers?.onStatus("open");
        }
        return;
      }
      cipher.open(new Uint8Array(e.data as ArrayBuffer)).then(
        ({ kind, data }) => {
          if (this.ws !== ws) return;
          if (kind === KIND_TEXT) {
            try {
              const v = JSON.parse(this.dec.decode(data));
              if (v && typeof v.type === "string") this.handlers?.onMessage(v as ServerMsg);
            } catch {
              /* not JSON: ignore */
            }
          } else {
            this.handlers?.onMessage(data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer);
          }
        },
        () => ws.close(), // bad frame: drop this leg and start over with fresh keys
      );
    };
    ws.onclose = (e: CloseEvent) => {
      if (this.ws !== ws) return;
      const wasReady = this.ready;
      this.ready = false;
      this.ws = null;
      if (wasReady || this.relayStatus === "open") this.handlers?.onStatus("closed");
      if (this.stopped) return;
      if (e.code === 4403 || e.code === 4401) {
        this.stopped = true;
        this.setRelayStatus("revoked");
        this.handlers?.onStatus("closed");
        return;
      }
      this.setRelayStatus(e.code === 4409 ? "pending" : e.code === 4404 ? "pc-offline" : "connecting");
      this.timer = setTimeout(() => void this.dial(), this.retryMs(this.attempt++, e.code));
    };
  }
}
